import { useNavigate } from "react-router-dom";
import {
  Settings,
  Puzzle,
  Code,
  Wifi,
  Shield,
  ClipboardList,
  Lightbulb,
  Cpu,
  Rocket } from
"lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Breadcrumb from "@/components/Breadcrumb";
import PageHero from "@/components/PageHero";
import CTABanner from "@/components/CTABanner";

const services = [
  {
    id: 1,
    icon: Settings,
    title: "CAD/CAM Automation",
    description: "Streamline repetitive design and manufacturing tasks with custom automation solutions. Reduce manual errors and increase productivity with scripted workflows.",
    features: ["Batch processing", "Drawing automation", "Toolpath generation", "Report generation"]
  },
  {
    id: 2,
    icon: Puzzle,
    title: "Custom Plugin Development",
    description: "Extend your CAD/CAM software capabilities with custom plugins and add-ins tailored to your specific design and manufacturing workflow requirements.",
    features: ["AutoCAD plugins", "SolidWorks add-ins", "NX customization", "CAM integration"]
  },
  {
    id: 3,
    icon: Wifi,
    title: "IoT Applications",
    description: "Connect your CAD/CAM workflows with IoT devices and sensors for real-time data integration and smart manufacturing solutions.",
    features: ["Sensor integration", "Real-time monitoring", "Data visualization", "Cloud connectivity"]
  },
  {
    id: 4,
    icon: Shield,
    title: "Web Applications Security",
    description: "Secure web applications for engineering data management, collaboration, and cloud-based design and manufacturing workflows.",
    features: ["Secure data transfer", "User authentication", "Access control", "Encryption solutions"]
  },
  {
    id: 5,
    icon: Code,
    title: "CAD/CAM Software Development",
    description: "Full-scale custom software solutions for engineering and manufacturing. From concept to deployment, we build powerful CAD/CAM tools.",
    features: ["Desktop applications", "Web-based tools", "Database systems", "Integration solutions"]
  }
];


const processSteps = [
{
  step: 1,
  icon: ClipboardList,
  title: "Requirement",
  description: "We analyze your needs and understand your workflow challenges"
},
{
  step: 2,
  icon: Lightbulb,
  title: "Planning",
  description: "We design the optimal solution architecture and timeline"
},
{
  step: 3,
  icon: Cpu,
  title: "Development",
  description: "Our team builds and tests the solution to your specifications"
},
{
  step: 4,
  icon: Rocket,
  title: "Delivery",
  description: "We deploy, train your team, and provide ongoing support"
}];


const ServicesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      
      <PageHero
        title="CAD/CAM Software Development Services & Engineering Solutions"
        subtitle="Custom solutions to streamline your design and manufacturing workflows, automate repetitive tasks, and build powerful CAD/CAM tools tailored to your needs."
        primaryCTA="Request Consultation"
        onPrimaryCTA={() => navigate("/contact")}
      >
        <div className="mb-6">
          <Breadcrumb />
        </div>
      </PageHero>

      {/* Services Grid */}
      <section data-testid="services-grid" className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

              Our Services
            </h2>
            <p className="mt-4 text-lg text-[#0F172A]/60 max-w-2xl mx-auto">
              End-to-end CAD/CAM solutions from automation to custom software development
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) =>
            <div
              key={service.id}
              data-testid={`service-card-${service.id}`}
              className="service-card bg-white border border-slate-200 rounded-2xl p-8 hover:border-[#06B6D4]/30 hover:shadow-lg transition-all duration-300">

                {/* Icon */}
                <div className="w-14 h-14 rounded-xl bg-[#F8FAFC] flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-[#06B6D4]" />
                </div>

                {/* Content */}
                <h3
                className="text-xl font-bold text-[#0F172A] mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

                  {service.title}
                </h3>
                <p className="text-[#0F172A]/60 leading-relaxed mb-6">
                  {service.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature, index) =>
                <span
                  key={index}
                  className="px-3 py-1 bg-[#F8FAFC] rounded-full text-sm text-[#0F172A]/70">

                      {feature}
                    </span>
                )}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section data-testid="process-section" className="py-16 lg:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F172A]"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

              Our Process
            </h2>
            <p className="mt-4 text-lg text-[#0F172A]/60 max-w-2xl mx-auto">
              A proven approach to delivering solutions that work
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((item) =>
            <div
              key={item.step}
              data-testid={`process-step-${item.step}`}
              className="relative bg-white border border-slate-200 rounded-2xl p-6 text-center">

                {/* Step Number */}
                <div className="w-12 h-12 rounded-full bg-[#2563EB] text-white flex items-center justify-center mx-auto mb-4 font-bold text-lg">
                  {item.step}
                </div>

                {/* Icon */}
                <div className="w-12 h-12 rounded-xl bg-[#F8FAFC] flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-6 h-6 text-[#06B6D4]" />
                </div>

                <h3
                className="text-lg font-semibold text-[#0F172A] mb-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>

                  {item.title}
                </h3>
                <p className="text-sm text-[#0F172A]/60">
                  {item.description}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTABanner
        title="Request a Consultation"
        subtitle="Let's discuss how we can help streamline your CAD workflows and build custom solutions for your team."
        buttonText="Contact Us"
        buttonLink="/contact" />


      <Footer />
    </div>);

};

export default ServicesPage;